import { useCallback, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { setInteractionError } from '@atlaskit/react-ufo/set-interaction-error';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { ExperiencePerformanceTypes, ExperienceTypes } from '@atlassian/ufo';
import { useUFOExperience } from '@atlassian/ufo-experimental';

import { QUICK_FIND_POST_QUERY_INTERACTION } from '../../../../common/constants/quick-find';
import { onSearchRequestCompleted } from '../../../../common/utils/quick-find/events/search-request-completed';
import { useIsHello, usePrimaryProduct, useSearchActions } from '../../../store';
import { useUserShardModel } from '../../../store/bootstrap';
import { useQuickFindAnalytics, useQuickFindAttributes } from '../../analytics';

export const DEBOUNCE_TIMEOUT = 250;

export const useQuickFindResultsCallbacks = () => {
	const [primaryProduct] = usePrimaryProduct();
	const serveUserShardModel = useUserShardModel();
	const { incrementQueryVersion } = useSearchActions();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const [isHello] = useIsHello();

	const {
		start: startPreQuery,
		success: successPreQuery,
		failure: failurePreQuery,
	} = useUFOExperience({
		id: `${primaryProduct}-pre-query-results-load`,
		component: 'search-dialog',
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	});

	const {
		start: startPostQuery,
		success: successPostQuery,
		failure: failurePostQuery,
		abort: abortPostQuery,
	} = useUFOExperience({
		id: `${primaryProduct}-post-query-results-load`,
		component: 'search-dialog',
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	});

	const onPreQueryStart = useCallback(() => {
		startPreQuery();
	}, [startPreQuery]);

	const onPreQuerySuccess = useCallback(() => {
		successPreQuery({
			metadata: { isQuickFind: true },
		});
	}, [successPreQuery]);

	const onPreQueryFailure = useCallback(() => {
		failurePreQuery();
	}, [failurePreQuery]);

	const onPostQueryChange = useCallback(() => {
		const useUfoMetrics = fg('quick_find_metrics_using_ufo_react');
		const useUfoPostQueryMetricInsideDebounce = fg('quick-find-post-query-metric-inside-debounce');
		if (useUfoMetrics && !useUfoPostQueryMetricInsideDebounce) {
			traceUFOPress(QUICK_FIND_POST_QUERY_INTERACTION);
		}
	}, []);

	const onPostQueryStart = useCallback(() => {
		const useUfoMetrics = fg('quick_find_metrics_using_ufo_react');
		const useUfoPostQueryMetricInsideDebounce = fg('quick-find-post-query-metric-inside-debounce');
		/**
		 * QS-7380 (LDR: https://hello.atlassian.net/wiki/x/QVo8QwE)
		 * Aborting on keypress fires a lot of dirty data volume to ufo. Instead we only instrument on query (inside the debounce), offsetting the start time by the debounce window because the keypress that trigger the debounce is the start of the interaction.
		 * fg('quick-find-post-query-metric-inside-debounce')
		 */
		if (useUfoMetrics && useUfoPostQueryMetricInsideDebounce) {
			const startTimestamp = performance.now() - DEBOUNCE_TIMEOUT;
			traceUFOPress(QUICK_FIND_POST_QUERY_INTERACTION, startTimestamp);
		}
		// ufo-v1 (above is react-ufo)
		const restartMetric = async () => {
			await abortPostQuery();
			await startPostQuery();
		};
		restartMetric();
	}, [abortPostQuery, startPostQuery]);

	const onPostQuerySuccess = useCallback(
		(duration: number, hasThirdPartyResults: boolean) => {
			incrementQueryVersion();

			successPostQuery({
				metadata: {
					isQuickFind: true,
					isHello,
					use3PResults: serveUserShardModel,
					hasThirdPartyResults,
				},
			});

			fireAnalyticsEvent(
				onSearchRequestCompleted({
					attributes: {
						...commonAttributes,
						postQueryRequestDurationMs: duration,
						hasThirdPartyResults,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[
			commonAttributes,
			fireAnalyticsEvent,
			incrementQueryVersion,
			nonPrivacySafeAttributes,
			isHello,
			serveUserShardModel,
			successPostQuery,
		],
	);

	const onPostQueryFailure = useCallback(() => {
		failurePostQuery();

		if (fg('quick_find_metrics_using_ufo_react')) {
			setInteractionError(QUICK_FIND_POST_QUERY_INTERACTION, {
				errorMessage: 'request failed',
				name: 'request failed',
			});
		}
	}, [failurePostQuery]);

	const onPostQueryCancel = useCallback(() => {
		abortPostQuery();
	}, [abortPostQuery]);

	return useMemo(
		() => ({
			onPreQueryStart,
			onPreQuerySuccess,
			onPreQueryFailure,
			onPostQueryChange,
			onPostQueryStart,
			onPostQuerySuccess,
			onPostQueryFailure,
			onPostQueryCancel,
		}),
		[
			onPostQueryCancel,
			onPostQueryFailure,
			onPostQueryChange,
			onPostQueryStart,
			onPostQuerySuccess,
			onPreQueryFailure,
			onPreQueryStart,
			onPreQuerySuccess,
		],
	);
};
